import { keyframes } from '@emotion/react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React from 'react';

const rotateOne = keyframes`
  0% {
    transform: rotateX(35deg) rotateY(-45deg) rotateZ(0deg);
  }
  100% {
    transform: rotateX(35deg) rotateY(-45deg) rotateZ(360deg);
  }
`;

const rotateTwo = keyframes`
  0% {
    transform: rotateX(50deg) rotateY(10deg) rotateZ(0deg);
  }
  100% {
    transform: rotateX(50deg) rotateY(10deg) rotateZ(360deg);
  }
`;

const rotateThree = keyframes`
  0% {
    transform: rotateX(35deg) rotateY(55deg) rotateZ(0deg);
  }
  100% {
    transform: rotateX(35deg) rotateY(55deg) rotateZ(360deg);
  }
`;

const SpinnerContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'floating',
})(({ theme, floating }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  pointerEvents: 'none',
  ...(floating ? {
    position: 'absolute',
    top: theme.spacing(6),
    left: theme.spacing(3),
    zIndex: 10,
    [theme.breakpoints.up('md')]: {
      top: theme.spacing(12),
      left: theme.spacing(10),
    },
  } : {}),
}));

const Spinner = styled('div')({
  position: 'relative',
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  perspective: '800px',
});

const SpinnerInner = styled('div', {
  shouldForwardProp: (prop) => prop !== 'reducedMotion',
})(({ theme, reducedMotion }) => {
  return {
    position: 'absolute',
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    borderColor: theme.palette.text.primary,

    '&.one': {
      left: '0%',
      top: '0%',
      animation: reducedMotion ? 'none' : `${rotateOne} 1s linear infinite`,
      borderBottom: `3px solid ${theme.palette.text.primary}`,
    },
    '&.two': {
      right: '0%',
      top: '0%',
      animation: reducedMotion ? 'none' : `${rotateTwo} 1s linear infinite`,
      borderRight: `3px solid ${theme.palette.text.primary}`,
    },
    '&.three': {
      right: '0%',
      bottom: '0%',
      animation: reducedMotion ? 'none' : `${rotateThree} 1s linear infinite`,
      borderTop: `3px solid ${theme.palette.text.primary}`,
    },
  };
});

const SpinnerText = styled('span')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums',
  textTransform: 'uppercase',
}));

const SpinnerLoader = ({
  label = 'loading...',
  progress = undefined,
  floating = false,
  reducedMotion = false,
}) => {
  const hasProgress = typeof progress === 'number';

  return (
    <SpinnerContainer floating={floating}>
      <Spinner aria-hidden="true">
        <SpinnerInner className="one" reducedMotion={reducedMotion} />
        <SpinnerInner className="two" reducedMotion={reducedMotion} />
        <SpinnerInner className="three" reducedMotion={reducedMotion} />
      </Spinner>
      <SpinnerText>{hasProgress ? `${label} ${progress}%` : label}</SpinnerText>
    </SpinnerContainer>
  );
};

SpinnerLoader.propTypes = {
  label: PropTypes.string,
  progress: PropTypes.number,
  floating: PropTypes.bool,
  reducedMotion: PropTypes.bool,
};

export default SpinnerLoader;
